import { useMsal } from '@azure/msal-react';
import {
    makeStyles,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
} from '@fluentui/react-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { ArbeitszeitItem } from '../../models/ArbeitszeitItem';
import type { ProjektItem } from '../../models/ProjektItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: tokens.spacingVerticalS,
    },
});

interface MitarbeiterArbeitszeitProps {
    project: ProjektItem | null;
}

// Helper function to format date to DD.MM.YYYY
const formatDate = (date: Date): string => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
};

// Helper function to format time to HH:MM
const formatTime = (date: Date): string => {
    if (!date) return '-';
    const d = new Date(date);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

// Helper function to calculate duration in HH:MM format
const calculateDuration = (von: Date, bis: Date): string => {
    if (!von || !bis) return '-';
    const diffMs = new Date(bis).getTime() - new Date(von).getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// Helper function to get year from date
const getYear = (date: Date): string => {
    if (!date) return '-';
    return new Date(date).getFullYear().toString();
};

// Helper function to get month name
const getMonthName = (date: Date): string => {
    if (!date) return '-';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthNames[new Date(date).getMonth()];
};

// Helper function to calculate total duration in HH:MM format
const calculateTotalDuration = (items: ArbeitszeitItem[]): string => {
    if (!items || items.length === 0) return '00:00';
    const totalMs = items.reduce((sum, item) => {
        if (!item.von || !item.bis) return sum;
        const diffMs = new Date(item.bis).getTime() - new Date(item.von).getTime();
        return sum + diffMs;
    }, 0);
    const totalMinutes = Math.floor(totalMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const EXCEL_COLUMNS: ExcelExportColumn<ArbeitszeitItem>[] = [
    { header: 'Employee', value: (item) => item.fullName, width: 30 },
    { header: 'Year', value: (item) => getYear(item.von), width: 10 },
    { header: 'Month', value: (item) => getMonthName(item.von), width: 10 },
    { header: 'Date', value: (item) => formatDate(item.von), width: 12 },
    { header: 'From', value: (item) => formatTime(item.von), width: 10 },
    { header: 'To', value: (item) => formatTime(item.bis), width: 10 },
    { header: 'Duration', value: (item) => calculateDuration(item.von, item.bis), width: 12 },
    {
        header: 'Hours',
        value: (item) =>
            item.von && item.bis
                ? Math.round(((new Date(item.bis).getTime() - new Date(item.von).getTime()) / 36e5) * 100) / 100
                : null,
        width: 10,
    },
    { header: 'Remark', value: (item) => item.bemerkung, width: 40 },
    { header: 'Work Package', value: (item) => item.arbeitspaketBezeichnung, width: 35 },
];

const MitarbeiterArbeitszeit: React.FC<MitarbeiterArbeitszeitProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [arbeitszeiten, setArbeitszeiten] = useState<ArbeitszeitItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Create dataService instance using MSAL context
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch arbeitszeiten
    const fetchArbeitszeiten = useCallback(async () => {
        if (!project?.projektID || !dataService) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const data = await dataService.getArbeitszeiten(project.projektID);
            setArbeitszeiten(data);
        } catch (err) {
            console.error('Error loading arbeitszeiten:', err);
            setError('Error loading working times');
            setArbeitszeiten([]);
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService]);

    useEffect(() => {
        fetchArbeitszeiten();
    }, [fetchArbeitszeiten]);

    if (!project) return null;

    // Loading state
    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading working times..." labelPosition="below" />
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState} style={{ color: tokens.colorStatusDangerForeground1 }}>
                    {error}
                </div>
            </div>
        );
    }

    // Empty state
    if (arbeitszeiten.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState}>
                    No working times recorded
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <ExcelExportButton
                    items={arbeitszeiten}
                    columns={EXCEL_COLUMNS}
                    fileName={`WorkingTimes_${project.projektID}_${new Date().toISOString().split('T')[0]}`}
                    sheetName="Working Times"
                />
            </div>
            <div className={styles.tableContainer}>
                <Table>
                    <TableHeader>
                        <TableRow style={{ backgroundColor: tokens.colorNeutralBackground2, borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                            <TableHeaderCell style={{ fontWeight: 700 }}>Employee</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>Year</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>Month</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>Date</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>From</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>To</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>Duration</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>Remark</TableHeaderCell>
                            <TableHeaderCell style={{ fontWeight: 700 }}>Work Package</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {arbeitszeiten.map((item) => (
                            <TableRow key={item.itemId} className={styles.tableRow}>
                                <TableCell>
                                    <TableCellLayout>
                                        {item.fullName}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {getYear(item.von)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {getMonthName(item.von)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {formatDate(item.von)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {formatTime(item.von)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {formatTime(item.bis)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {calculateDuration(item.von, item.bis)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {item.bemerkung || '-'}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {item.arbeitspaketBezeichnung || '-'}
                                    </TableCellLayout>
                                </TableCell>
                            </TableRow>
                        ))}
                        {/* Summary Row */}
                        <TableRow style={{ fontWeight: 'bold', backgroundColor: tokens.colorNeutralBackground2 }}>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout>
                                    <strong>{calculateTotalDuration(arbeitszeiten)}</strong>
                                </TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                            <TableCell>
                                <TableCellLayout></TableCellLayout>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default MitarbeiterArbeitszeit;
